<template>
  <div 
    class="board-column"
    :class="[column.borderClass, { 'drop-zone-active': isDragOver }]"
    @dragover.prevent="onDragOver"
    @dragleave="onDragLeave"
    @drop.prevent="onDrop"
  >
    <!-- Column Header -->
    <div class="column-header">
      <div class="d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center column-title-group">
          <h6 class="mb-0 fw-bold me-2">{{ column.title.trim() }}</h6>
          <span class="info-icon count-indicator" :title="column.subTitle || ''">
            {{ column.list.length }}
          </span>
        </div>
        <button
          v-if="enableSettings"
          class="btn btn-link p-0 gear-btn"
          type="button"
          @click.stop="openSettings"
          :title="'Configure ' + column.title"
        >
          <i class="fa fa-cog"></i>
        </button>
      </div>
    </div>

    <!-- Column Content -->
    <div class="column-content">
      <transition-group 
        name="job-card"
        tag="div"
        class="job-list"
        @before-enter="beforeEnter"
        @enter="enter"
        @leave="leave"
      >
        <JobCard
          v-for="job in column.list"
          :key="job.id"
          :job="job"
          :is-selected="isJobSelected(job)"
          :data-job-id="job.id"
          @click="onJobClick(job)"
          @dragstart="onJobDragStart"
          @dragend="onJobDragEnd"
        />
      </transition-group>
      
      <!-- Empty state -->
      <div v-if="column.list.length === 0" class="empty-column">
        <div class="text-center text-muted py-4">
          <i class="fa fa-inbox fa-2x mb-2"></i>
          <p class="mb-0">No jobs in this status</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JobCard from './JobCard.vue';

export default {
  name: 'BoardColumn',
  components: {
    JobCard
  },
  props: {
    column: {
      type: Object,
      required: true
    },
    enableSettings: {
      type: Boolean,
      default: true
    },
    selectedJob: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isDragOver: false
    };
  },
  methods: {
    isJobSelected(job) {
      return this.selectedJob && 
             (this.selectedJob.intProjectID === job.intProjectID || 
              this.selectedJob.id === job.id);
    },

    onJobClick(job) {
      this.$emit('job-click', job);
    },

    onJobDragStart(job) {
      this.$emit('job-drag-start', { job, columnId: this.column.id });
    },

    onJobDragEnd() {
      // Reset any drag states if needed
    },

    onDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },

    onDragLeave(event) {
      // Only remove drag-over state if leaving the column entirely
      if (!this.$el.contains(event.relatedTarget)) {
        this.isDragOver = false;
      }
    },

    onDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      
      this.$emit('job-drop', {
        columnId: this.column.id
      });
    },
    openSettings() {
      this.$emit('open-column-settings', this.column);
    },

    // Animation hooks
    beforeEnter(el) {
      el.style.opacity = '0';
      el.style.transform = 'translateY(-10px)';
    },

    enter(el, done) {
      // Force reflow
      el.offsetHeight;
      
      el.style.transition = 'all 0.3s ease';
      el.style.opacity = '1';
      el.style.transform = 'translateY(0)';
      
      setTimeout(done, 300);
    },

    leave(el, done) {
      el.style.transition = 'all 0.3s ease';
      el.style.opacity = '0';
      el.style.transform = 'translateX(20px)';
      
      setTimeout(done, 300);
    }
  }
};
</script>

<style scoped>
.board-column {
  flex: 1 1 0;
  min-width: 0;
  background: #fff;
  border-radius: 0.375rem;
  border: none;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  box-shadow: 0 1px 2px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04);
  transition: all 0.2s ease;
}

.column-header {
  flex-shrink: 0;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #eee;
  background: transparent;
}

.column-content {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem;
  position: relative;
}

.job-list {
  min-height: 100%;
}

/* Drop zone styling */
.drop-zone-active {
  box-shadow: 0 4px 20px rgba(13, 98, 255, 0.3);
  border: 2px dashed #0d62ff !important;
  background: rgba(13, 98, 255, 0.02);
}

.drop-zone-active .column-header {
  background: rgba(13, 98, 255, 0.05);
}

/* Empty state */
.empty-column {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
}

/* Border color classes for columns */
.border-top-orange {
  border-top: 4px solid #fd7e14 !important;
}

.border-top-blue {
  border-top: 4px solid #007bff !important;
}

.border-top-purple {
  border-top: 4px solid #6f42c1 !important;
}

.border-top-yellow {
  border-top: 4px solid #ffc107 !important;
}

.border-top-grey {
  border-top: 4px solid #6c757d !important;
}

.border-top-green {
  border-top: 4px solid #28a745 !important;
}

.border-top-teal {
  border-top: 4px solid #20c997 !important;
}

/* Count indicator */
.info-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 999px; /* pill / circle */
  background: #e9ecef;
  font-size: 11px;
  font-weight: 600;
  color: #555;
  cursor: help;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease;
}

.info-icon:hover {
  background: #6e0270;
  color: #fff;
}

.count-indicator {
  cursor: default;
  margin-left: 0.5rem; /* space between title and number */
}

.gear-btn {
  color: #b5bcc2 !important; /* lighter default */
  font-size: 14px;
  line-height: 1;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.gear-btn:hover, .gear-btn:focus {
  color: #6c757d !important; /* darken on hover */
  text-decoration: none !important;
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
}

.column-title-group {
  display: flex;
  align-items: center;
}

/* Scrollbar styling */
.column-content::-webkit-scrollbar {
  width: 6px;
}

.column-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.column-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.column-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Animation classes */
.job-card-enter-active, .job-card-leave-active {
  transition: all 0.3s ease;
}

.job-card-enter, .job-card-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.job-card-move {
  transition: transform 0.3s ease;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .board-column {
    height: calc(100vh - 220px);
    min-width: 280px;
  }
}

@media (max-width: 576px) {
  .board-column {
    height: calc(100vh - 240px);
    min-width: 260px;
  }
}
</style>
